import { memo, type CSSProperties } from 'react';

type SvgStyle = CSSProperties & { '--play'?: 'running' | 'paused' };

interface ThinkingOwlProps {
  /**
   * 'thinking': The owl animates (drawing lines, fading in eyes/beak).
   * 'complete': The owl is static and fully visible.
   */
  mode: 'thinking' | 'complete';
}

export const ThinkingOwl = memo(function ThinkingOwl({ mode }: ThinkingOwlProps) {
  const className = [
    'h-8 w-8 thinking-owl',
    mode === 'complete' ? 'thinking-owl--complete' : 'thinking-owl--thinking',
  ].join(' ');

  return (
    <svg
      viewBox="0 0 2480 2480"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      /* Controls the animation state via CSS variable */
      style={{ '--play': mode === 'thinking' ? 'running' : 'paused' } as SvgStyle}
    >
      <defs>
        <style>{`
      /* =========================================
         1. STROKE ANIMATION (Head, Forehead, Circle)
         ========================================= */
      .anim-stroke {
        stroke: rgb(137, 180, 214);
        stroke-width: 35px;
        stroke-linecap: round;
        stroke-linejoin: round;
        fill: none;
        
        /* The "drawing" effect is achieved by manipulating the dash offset */
        stroke-dasharray: var(--len);
        stroke-dashoffset: var(--len);
        animation: drawLine 4s ease-in-out infinite;
        animation-play-state: var(--play, running);
      }

      /* =========================================
         2. FILL ANIMATION (Eyes & Beak)
         ========================================= */
      .anim-fill {
        fill: rgb(137, 180, 214);
        stroke: none;
        /* CRITICAL: Ensures the "highlight" hole in the eye is transparent */
        fill-rule: evenodd; 
        opacity: 0;
        
        /* Fade-in/Pop-up effect */
        animation: fadeInOut 4s ease-in-out infinite;
        animation-play-state: var(--play, running);
      }

      /* --- Static State (Completion) --- */
      /* When complete, stop animations and show everything fully */
      .thinking-owl--complete .anim-stroke {
        animation: none;
        stroke-dashoffset: 0;
      }
      .thinking-owl--complete .anim-fill {
        animation: none;
        opacity: 1;
      }

      /* =========================================
         KEYFRAMES
         ========================================= */
      
      /* Animates the drawing of the lines */
      @keyframes drawLine {
        0% { stroke-dashoffset: var(--len); }   /* Invisible */
        35% { stroke-dashoffset: 0; }           /* Fully drawn */
        65% { stroke-dashoffset: 0; }           /* Pause/Hold */
        100% { stroke-dashoffset: var(--len); } /* Un-draw/Disappear */
      }

      /* Animates the opacity of the filled parts */
      @keyframes fadeInOut {
        /* Timing is tight to create a "pop" effect after lines are drawn */
        0%, 30% { opacity: 0; }             /* Wait */
        40% { opacity: 1; }                 /* Fade In */
        65% { opacity: 1; }                 /* Hold */
        85%, 100% { opacity: 0; }           /* Fade Out */
      }

      /* =========================================
         TIMING & SEQUENCE
         ========================================= */
      
      /* -- Line Elements (Base Structure) -- */
      .svg-elem-1 { --len: 365px; animation-delay: 0s; }   /* Head Top */
      .svg-elem-2 { --len: 365px; animation-delay: 0.1s; } /* Head Mirrored */
      .svg-elem-6 { --len: 366px; animation-delay: 0.2s; } /* Forehead */
      .svg-elem-7 { --len: 1008px; animation-delay: 0.3s; }/* Outer Circle */

      /* -- Filled Elements (Staggered Appearance) -- */
      /* Sequence: Right Eye -> Left Eye -> Nose */
      
      /* 1. Right Eye (Viewer's Perspective) */
      .svg-elem-3 { animation-delay: 0.3s; } 
      
      /* 2. Left Eye (Viewer's Perspective) */
      .svg-elem-4 { animation-delay: 0.45s; } 
      
      /* 3. Beak/Nase */
      .svg-elem-5 { animation-delay: 0.6s; } 

    `}</style>
      </defs>

      <g transform="matrix(2.994888,0,0,2.994888,-1100.504601,-1001.673313)">
        {/* --- STROKE ELEMENTS --- */}

        {/* Head Top Line */}
        <path
          className="anim-stroke svg-elem-1"
          d="M540,504C542.058,518.658 550.243,537.284 567.857,552.532C580.132,563.157 599.072,572.046 615,577C634.251,582.988 649.363,583.455 661.622,586.204C683.101,591.02 702.363,598.727 717,608C750.016,628.917 763.37,653.632 772,677C777.128,690.886 780.376,717.629 782,731"
        />
        {/* Head Mirrored Line */}
        <g transform="matrix(-1,0,0,1,1563,0)">
          <path
            className="anim-stroke svg-elem-2"
            d="M540,504C542.058,518.658 550.243,537.284 567.857,552.532C580.132,563.157 599.072,572.046 615,577C634.251,582.988 649.363,583.455 661.622,586.204C683.101,591.02 702.363,598.727 717,608C750.016,628.917 763.37,653.632 772,677C777.128,690.886 779.416,717.629 780.56,731"
          />
        </g>
        {/* Forehead Line */}
        <g transform="matrix(0.991273,0,0,0.940283,6.81994,30.69436)">
          <path
            className="anim-stroke svg-elem-6"
            strokeWidth={36.23}
            d="M616.493,578.285C659.616,535.468 714.991,514 780.5,514C847.032,514 903.238,534.302 946.516,578.304"
          />
        </g>
        {/* Outer Circle Line */}
        <path
          className="anim-stroke svg-elem-7"
          d="M986.281,631.964C1008.922,668.436 1020,708.453 1020,754.5C1020,886.132 913.132,993 781.5,993C649.868,993 543,886.132 543,754.5C543,706.82 551.022,670.389 575.163,633.109"
        />

        {/* --- FILLED ELEMENTS (Staggered Fade-in) --- */}

        {/* Right Eye (elem-3) */}
        <g transform="matrix(1.074074,0,0,1.074074,170.814815,-58.62963)">
          <path
            className="anim-fill svg-elem-3"
            d="M 677,752 C 654,752 636,733 636,710 C 636,687 654,668 677,668 C 700,668 718,687 718,710 C 718,733 700,752 677,752 Z M 705,695 C 705,687 698,680 690,680 C 682,680 675,687 675,695 C 675,703 682,710 690,710 C 698,710 705,703 705,695 Z"
          />
        </g>

        {/* Left Eye (elem-4) */}
        <g transform="matrix(1.074074,0,0,1.074074,-63.185185,-58.62963)">
          <path
            className="anim-fill svg-elem-4"
            d="M 677,752 C 654,752 636,733 636,710 C 636,687 654,668 677,668 C 700,668 718,687 718,710 C 718,733 700,752 677,752 Z M 705,695 C 705,687 698,680 690,680 C 682,680 675,687 675,695 C 675,703 682,710 690,710 C 698,710 705,703 705,695 Z"
          />
        </g>

        {/* Beak / Nose (elem-5) */}
        <g transform="matrix(-1.24116,-0,0,-1.079483,1741.537208,1702.328497)">
          <path
            className="anim-fill svg-elem-5"
            d="M779.96,775.661L811.46,837.661C812.812,840.321 812.823,843.61 811.489,846.282C810.155,848.955 807.68,850.603 805,850.603L742,850.603C739.32,850.603 736.845,848.955 735.511,846.282C734.177,843.61 734.188,840.321 735.54,837.661L767.04,775.661C768.381,773.021 770.841,771.397 773.5,771.397C776.159,771.397 778.619,773.021 779.96,775.661Z"
          />
        </g>
      </g>
    </svg>
  );
});
